
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DemandChargeRate, DemandRatesSavePayload, EnergyRate, EnergyRatesSavePayload, OtherChargeItem, ServiceChargeItem, NoteItem, UtilitySchedule, ScheduleMetadata } from '../../../setup/utility/interfaces/utility.interface';
import { UtilityService } from '../../../setup/utility/services/utility.service';
import { DatepickerComponent } from '../datepicker/datepicker.component';
import { EditableTableSectionComponent } from '../editable-table-section/editable-table-section.component';

/** Nominal year for season date picker (month/day only matter). */
const NOMINAL_YEAR = 2024;

@Component({
  selector: 'app-utility-rates-display',
  standalone: true,
  imports: [FormsModule, DatepickerComponent, EditableTableSectionComponent],
  templateUrl: './utility-rates-display.component.html',
  styleUrl: './utility-rates-display.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UtilityRatesDisplayComponent implements OnChanges {
  private readonly utilityService = inject(UtilityService);
  private readonly monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ] as const;

  @Input() utilitySchedule: UtilitySchedule | null = null;
  @Input() demandRates: DemandChargeRate[] = [];
  @Input() energyRates: EnergyRate[] = [];
  @Input() isLoading = false;
  @Input() isSavingDemandRates = false;
  @Input() isSavingEnergyRates = false;
  @Input() isSavingServiceCharges = false;
  @Input() isSavingOtherCharges = false;
  @Input() isSavingNotes = false;
  @Input() scheduleMetadata: ScheduleMetadata | null = null;
  @Input() autoLoadScheduleMetadata = true;
  @Input() readOnly = false;

  @Output() saveDemandRates = new EventEmitter<DemandRatesSavePayload>();
  @Output() saveEnergyRates = new EventEmitter<EnergyRatesSavePayload>();
  @Output() saveServiceCharges = new EventEmitter<ScheduleMetadata>();
  @Output() saveOtherCharges = new EventEmitter<ScheduleMetadata>();
  @Output() saveNotes = new EventEmitter<ScheduleMetadata>();

  // Toggle states - Demand and Energy visible by default, others hidden
  showDemandRates = signal(true);

  // Demand rates edit mode
  editingDemandRates = signal(false);
  demandRatesEdit = signal<DemandChargeRate[]>([]);
  /** Season date range per row for datepicker binding (nominal year YYYY-MM-DD). Null = All year or not set. */
  demandRatesSeasonRanges = signal<Array<{ fromDate: string | null; toDate: string | null } | null>>([]);
  /** IDs of demand rates marked for deletion (removed from table, sent as toDelete on save). */
  demandRatesToDelete = signal<number[]>([]);

  // Energy rates edit mode
  editingEnergyRates = signal(false);
  energyRatesEdit = signal<EnergyRate[]>([]);
  energyRatesSeasonRanges = signal<Array<{ fromDate: string | null; toDate: string | null } | null>>([]);
  /** IDs of energy rates marked for deletion (removed from table, sent as toDelete on save). */
  energyRatesToDelete = signal<number[]>([]);

  // Service charges edit mode
  editingServiceCharges = signal(false);
  serviceChargesEdit = signal<ServiceChargeItem[]>([]);
  /** Charge unit options for service charges and other charges. Default "per month". */
  readonly serviceChargeUnitOptions: { label: string; value: string }[] = [
    { label: 'per month', value: 'per month' },
    { label: 'per day', value: 'per day' },
  ];

  // Other charges edit mode
  editingOtherCharges = signal(false);
  otherChargesEdit = signal<OtherChargeItem[]>([]);

  // Notes edit mode
  editingNotes = signal(false);
  notesEdit = signal<NoteItem[]>([]);

  /** Options for the Applies column in demand/energy rate edit. Value is days_of_week_holidays string. */
  readonly appliesOptions: { label: string; value: string }[] = [
    { label: 'Every day', value: 'YYYYYYYY' },
    { label: 'Weekdays', value: 'NYYYYYNN' },
    { label: 'Weekends / Holidays', value: 'YNNNNNYY' },
  ];

  readonly seasonTypeAllYear = 'all_year';
  readonly seasonTypeDateRange = 'date_range';

  /** Index of the demand rate row whose season datepicker is expanded; null = all collapsed. */
  expandedSeasonRowIndex = signal<number | null>(null);
  /** Index of the energy rate row whose season datepicker is expanded; null = all collapsed. */
  expandedEnergySeasonRowIndex = signal<number | null>(null);

  showEnergyRates = signal(true);
  showServiceCharges = signal(false);
  showOtherCharges = signal(false);
  showNotes = signal(false);

  // Schedule metadata - can be provided as input or loaded separately
  private _scheduleMetadata = signal<ScheduleMetadata | null>(null);
  isLoadingMetadata = signal(false);

  toggleDemandRates() {
    this.showDemandRates.set(!this.showDemandRates());
  }

  toggleEnergyRates() {
    this.showEnergyRates.set(!this.showEnergyRates());
  }

  toggleServiceCharges() {
    this.showServiceCharges.set(!this.showServiceCharges());
  }

  toggleOtherCharges() {
    this.showOtherCharges.set(!this.showOtherCharges());
  }

  toggleNotes() {
    this.showNotes.set(!this.showNotes());
  }

  enterDemandRatesEdit(): void {
    this.demandRatesEdit.set(this.demandRates.map((r) => ({ ...r })));
    const ranges = this.demandRates.map((r) =>
      this.isAllYearSeasonRate(r) ? null : this.rateToSeasonRangeRate(r)
    );
    this.demandRatesSeasonRanges.set(ranges);
    this.demandRatesToDelete.set([]);
    this.expandedSeasonRowIndex.set(null);
    this.editingDemandRates.set(true);
  }

  cancelDemandRatesEdit(): void {
    this.editingDemandRates.set(false);
    this.demandRatesEdit.set([]);
    this.demandRatesSeasonRanges.set([]);
    this.demandRatesToDelete.set([]);
    this.expandedSeasonRowIndex.set(null);
  }

  saveDemandRatesEdit(): void {
    const edited = this.demandRatesEdit();
    const original = this.demandRates;
    const toCreate = edited.filter((r) => r.id === 0);
    const toUpdate = edited.filter((r) => {
      if (r.id === 0) return false;
      const orig = original.find((o) => o.id === r.id);
      return orig != null && this.demandRateDataChanged(orig, r);
    });
    const toDelete = this.demandRatesToDelete();
    this.saveDemandRates.emit({ toCreate, toUpdate, toDelete });
    this.editingDemandRates.set(false);
    this.demandRatesEdit.set([]);
    this.demandRatesToDelete.set([]);
  }

  deleteDemandRateRow(index: number): void {
    const current = this.demandRatesEdit();
    const rate = current[index];
    const copy = current.slice();
    copy.splice(index, 1);
    this.demandRatesEdit.set(copy);
    const ranges = this.demandRatesSeasonRanges().slice();
    ranges.splice(index, 1);
    this.demandRatesSeasonRanges.set(ranges);
    if (rate.id !== 0) {
      this.demandRatesToDelete.set([...this.demandRatesToDelete(), rate.id]);
    }
    if (this.expandedSeasonRowIndex() === index) {
      this.expandedSeasonRowIndex.set(null);
    } else if (this.expandedSeasonRowIndex() !== null && this.expandedSeasonRowIndex()! > index) {
      this.expandedSeasonRowIndex.set(this.expandedSeasonRowIndex()! - 1);
    }
  }

  /** Normalize time to HH:MM for comparison (API may return HH:MM:SS). */
  private normalizeTime(t?: string | null): string {
    if (!t) return '';
    return t.slice(0, 5); // "HH:MM" or "00:00" from "00:00:00"
  }

  /** True if any data field (excluding id, utility_schedule_id, created, modified, manually_entered) differs. */
  private demandRateDataChanged(original: DemandChargeRate, edited: DemandChargeRate): boolean {
    return (
      (original.rate_description ?? '') !== (edited.rate_description ?? '') ||
      (original.season_description ?? '') !== (edited.season_description ?? '') ||
      (original.demand_charge_rate_kw ?? null) !== (edited.demand_charge_rate_kw ?? null) ||
      (original.min_kv ?? null) !== (edited.min_kv ?? null) ||
      (original.max_kv ?? null) !== (edited.max_kv ?? null) ||
      (original.start_month ?? null) !== (edited.start_month ?? null) ||
      (original.start_day ?? null) !== (edited.start_day ?? null) ||
      (original.end_month ?? null) !== (edited.end_month ?? null) ||
      (original.end_day ?? null) !== (edited.end_day ?? null) ||
      this.normalizeTime(original.start_time) !== this.normalizeTime(edited.start_time) ||
      this.normalizeTime(original.end_time) !== this.normalizeTime(edited.end_time) ||
      (original.days_of_week_holidays ?? '') !== (edited.days_of_week_holidays ?? '')
      || (original.start_kw ?? null) !== (edited.start_kw ?? null)
      || (original.end_kw ?? null) !== (edited.end_kw ?? null)
    );
  }

  enterEnergyRatesEdit(): void {
    this.energyRatesEdit.set(this.energyRates.map((r) => ({ ...r })));
    const ranges = this.energyRates.map((r) =>
      this.isAllYearSeasonRate(r) ? null : this.rateToSeasonRangeRate(r)
    );
    this.energyRatesSeasonRanges.set(ranges);
    this.energyRatesToDelete.set([]);
    this.expandedEnergySeasonRowIndex.set(null);
    this.editingEnergyRates.set(true);
  }

  cancelEnergyRatesEdit(): void {
    this.editingEnergyRates.set(false);
    this.energyRatesEdit.set([]);
    this.energyRatesSeasonRanges.set([]);
    this.energyRatesToDelete.set([]);
    this.expandedEnergySeasonRowIndex.set(null);
  }

  saveEnergyRatesEdit(): void {
    const edited = this.energyRatesEdit();
    const original = this.energyRates;
    const toCreate = edited.filter((r) => r.id === 0);
    const toUpdate = edited.filter((r) => {
      if (r.id === 0) return false;
      const orig = original.find((o) => o.id === r.id);
      return orig != null && this.energyRateDataChanged(orig, r);
    });
    const toDelete = this.energyRatesToDelete();
    this.saveEnergyRates.emit({ toCreate, toUpdate, toDelete });
    this.editingEnergyRates.set(false);
    this.energyRatesEdit.set([]);
    this.energyRatesSeasonRanges.set([]);
    this.energyRatesToDelete.set([]);
    this.expandedEnergySeasonRowIndex.set(null);
  }

  deleteEnergyRateRow(index: number): void {
    const current = this.energyRatesEdit();
    const rate = current[index];
    const copy = current.slice();
    copy.splice(index, 1);
    this.energyRatesEdit.set(copy);
    const ranges = this.energyRatesSeasonRanges().slice();
    ranges.splice(index, 1);
    this.energyRatesSeasonRanges.set(ranges);
    if (rate.id !== 0) {
      this.energyRatesToDelete.set([...this.energyRatesToDelete(), rate.id]);
    }
    if (this.expandedEnergySeasonRowIndex() === index) {
      this.expandedEnergySeasonRowIndex.set(null);
    } else if (this.expandedEnergySeasonRowIndex() !== null && this.expandedEnergySeasonRowIndex()! > index) {
      this.expandedEnergySeasonRowIndex.set(this.expandedEnergySeasonRowIndex()! - 1);
    }
  }

  /** True if any data field (excluding id, utility_schedule_id, created, modified, manually_entered) differs. */
  private energyRateDataChanged(original: EnergyRate, edited: EnergyRate): boolean {
    return (
      (original.rate_description ?? '') !== (edited.rate_description ?? '') ||
      (original.season_description ?? '') !== (edited.season_description ?? '') ||
      (original.energy_rate_kwh ?? null) !== (edited.energy_rate_kwh ?? null) ||
      (original.min_kv ?? null) !== (edited.min_kv ?? null) ||
      (original.max_kv ?? null) !== (edited.max_kv ?? null) ||
      (original.start_month ?? null) !== (edited.start_month ?? null) ||
      (original.start_day ?? null) !== (edited.start_day ?? null) ||
      (original.end_month ?? null) !== (edited.end_month ?? null) ||
      (original.end_day ?? null) !== (edited.end_day ?? null) ||
      this.normalizeTime(original.start_time) !== this.normalizeTime(edited.start_time) ||
      this.normalizeTime(original.end_time) !== this.normalizeTime(edited.end_time) ||
      (original.days_of_week_holidays ?? '') !== (edited.days_of_week_holidays ?? '')
      || (original.start_kwh ?? null) !== (edited.start_kwh ?? null)
      || (original.end_kwh ?? null) !== (edited.end_kwh ?? null)
    );
  }

  /** True if rate description has at least one non-whitespace character. */
  isRateDescriptionValid(rate: DemandChargeRate | EnergyRate): boolean {
    return ((rate.rate_description ?? '').trim().length > 0);
  }

  /** True if demand rate value is a valid finite number (positive or negative). */
  isRateValueValid(rate: DemandChargeRate): boolean {
    const v = rate.demand_charge_rate_kw;
    return typeof v === 'number' && Number.isFinite(v);
  }

  /** True if energy rate value is a valid finite number (positive or negative). */
  isEnergyRateValueValid(rate: EnergyRate): boolean {
    const v = rate.energy_rate_kwh;
    return typeof v === 'number' && Number.isFinite(v);
  }

  /** True if there is something to save (rows to create/update, or rows to delete) and every remaining row is valid. */
  isDemandRatesEditValid(): boolean {
    const rates = this.demandRatesEdit();
    const toDelete = this.demandRatesToDelete();
    const hasWork = rates.length > 0 || toDelete.length > 0;
    if (!hasWork) return false;
    return rates.every((r) => this.isRateDescriptionValid(r) && this.isRateValueValid(r));
  }

  /** True if there is something to save (rows to create/update, or rows to delete) and every remaining row is valid. */
  isEnergyRatesEditValid(): boolean {
    const rates = this.energyRatesEdit();
    const toDelete = this.energyRatesToDelete();
    const hasWork = rates.length > 0 || toDelete.length > 0;
    if (!hasWork) return false;
    return rates.every((r) => this.isRateDescriptionValid(r) && this.isEnergyRateValueValid(r));
  }

  addDemandRateRow(): void {
    const scheduleId = this.utilitySchedule?.id ?? 0;
    const newRate: DemandChargeRate = {
      id: 0,
      utility_schedule_id: scheduleId,
      rate_description: '',
      season_description: 'All year',
      start_month: 1,
      start_day: 1,
      end_month: 12,
      end_day: 31,
      demand_charge_rate_kw: undefined,
      min_kv: undefined,
      max_kv: undefined,
      start_time: '00:00',
      end_time: '23:59',
      days_of_week_holidays: 'YYYYYYYY', // Every day
      start_kw: undefined,
      end_kw: undefined,
    };
    this.demandRatesEdit.set([...this.demandRatesEdit(), newRate]);
    this.demandRatesSeasonRanges.set([...this.demandRatesSeasonRanges(), null]);
  }

  addEnergyRateRow(): void {
    const scheduleId = this.utilitySchedule?.id ?? 0;
    const newRate: EnergyRate = {
      id: 0,
      utility_schedule_id: scheduleId,
      rate_description: '',
      season_description: 'All year',
      start_month: 1,
      start_day: 1,
      end_month: 12,
      end_day: 31,
      energy_rate_kwh: undefined,
      min_kv: undefined,
      max_kv: undefined,
      start_time: '00:00',
      end_time: '23:59',
      days_of_week_holidays: 'YYYYYYYY', // Every day
      start_kwh: undefined,
      end_kwh: undefined,
    };
    this.energyRatesEdit.set([...this.energyRatesEdit(), newRate]);
    this.energyRatesSeasonRanges.set([...this.energyRatesSeasonRanges(), null]);
  }

  enterServiceChargesEdit(): void {
    const list = this.serviceCharges.map((c) => ({ ...c }));
    this.serviceChargesEdit.set(list);
    this.editingServiceCharges.set(true);
  }

  cancelServiceChargesEdit(): void {
    this.editingServiceCharges.set(false);
    this.serviceChargesEdit.set([]);
  }

  private serviceChargeUnchanged(orig: ServiceChargeItem, edited: ServiceChargeItem): boolean {
    const d = (a: string | undefined, b: string | undefined) => (a ?? '').trim() === (b ?? '').trim();
    return d(orig.Description, edited.Description) && d(String(orig.Rate), String(edited.Rate)) && d(orig.ChargeUnit, edited.ChargeUnit);
  }

  saveServiceChargesEdit(): void {
    const edited = this.serviceChargesEdit();
    const original = this.serviceCharges;
    const scheduleId = String(this.utilitySchedule?.id ?? '');
    const service_charges: ServiceChargeItem[] = edited.map((c, i) => {
      const isNewOrModified = i >= original.length || !this.serviceChargeUnchanged(original[i], c);
      return {
        Pending: c.Pending ?? '',
        ScheduleID: c.ScheduleID || scheduleId,
        Description: (c.Description ?? '').trim(),
        Rate: String(c.Rate ?? '').trim(),
        ChargeUnit: c.ChargeUnit ?? 'per month',
        manually_entered: isNewOrModified ? true : (original[i].manually_entered ?? false),
      };
    });
    const meta = this.scheduleMetadataValue;
    const updated: ScheduleMetadata = {
      service_charges,
      other_charges: meta?.other_charges ?? [],
      notes: meta?.notes ?? [],
      reactive_demand: meta?.reactive_demand ?? [],
      modifies_schedule: meta?.modifies_schedule ?? [],
      tax_info: meta?.tax_info ?? [],
      percentages: meta?.percentages ?? [],
    };
    this.saveServiceCharges.emit(updated);
    this.editingServiceCharges.set(false);
    this.serviceChargesEdit.set([]);
  }

  addServiceChargeRow(): void {
    const scheduleId = String(this.utilitySchedule?.id ?? '');
    const newCharge: ServiceChargeItem = {
      Pending: '',
      ScheduleID: scheduleId,
      Description: '',
      Rate: '',
      ChargeUnit: 'per month',
      manually_entered: true,
    };
    this.serviceChargesEdit.set([...this.serviceChargesEdit(), newCharge]);
  }

  deleteServiceChargeRow(index: number): void {
    const copy = this.serviceChargesEdit().slice();
    copy.splice(index, 1);
    this.serviceChargesEdit.set(copy);
  }

  updateServiceChargeField(index: number, field: keyof ServiceChargeItem, value: string | undefined): void {
    const current = this.serviceChargesEdit();
    const copy = current.slice();
    copy[index] = { ...copy[index], [field]: value };
    this.serviceChargesEdit.set(copy);
  }

  isServiceChargeDescriptionValid(charge: ServiceChargeItem): boolean {
    return ((charge.Description ?? '').trim().length > 0);
  }

  isServiceChargeRateValid(charge: ServiceChargeItem): boolean {
    const s = (charge.Rate ?? '').trim();
    if (s === '') return false;
    const n = Number(s);
    return Number.isFinite(n);
  }

  isServiceChargesEditValid(): boolean {
    const list = this.serviceChargesEdit();
    if (list.length === 0) return false;
    return list.every((c) => this.isServiceChargeDescriptionValid(c) && this.isServiceChargeRateValid(c));
  }

  enterOtherChargesEdit(): void {
    this.otherChargesEdit.set(this.otherCharges.map((c) => ({ ...c })));
    this.editingOtherCharges.set(true);
  }

  cancelOtherChargesEdit(): void {
    this.editingOtherCharges.set(false);
    this.otherChargesEdit.set([]);
  }

  private otherChargeUnchanged(orig: OtherChargeItem, edited: OtherChargeItem): boolean {
    const d = (a: string | undefined, b: string | undefined) => (a ?? '').trim() === (b ?? '').trim();
    return d(orig.Description, edited.Description) && d(orig.ChargeType, edited.ChargeType) && d(orig.ChargeUnit, edited.ChargeUnit);
  }

  saveOtherChargesEdit(): void {
    const edited = this.otherChargesEdit();
    const original = this.otherCharges;
    const scheduleId = String(this.utilitySchedule?.id ?? '');
    const other_charges: OtherChargeItem[] = edited.map((c, i) => {
      const isNewOrModified = i >= original.length || !this.otherChargeUnchanged(original[i], c);
      return {
        Pending: c.Pending ?? '',
        ScheduleID: c.ScheduleID || scheduleId,
        ChargeType: (c.ChargeType ?? '').trim(),
        Description: (c.Description ?? '').trim(),
        ChargeUnit: c.ChargeUnit ?? 'per month',
        manually_entered: isNewOrModified ? true : (original[i].manually_entered ?? false),
      };
    });
    const meta = this.scheduleMetadataValue;
    const updated: ScheduleMetadata = {
      service_charges: meta?.service_charges ?? [],
      other_charges,
      notes: meta?.notes ?? [],
      reactive_demand: meta?.reactive_demand ?? [],
      modifies_schedule: meta?.modifies_schedule ?? [],
      tax_info: meta?.tax_info ?? [],
      percentages: meta?.percentages ?? [],
    };
    this.saveOtherCharges.emit(updated);
    this.editingOtherCharges.set(false);
    this.otherChargesEdit.set([]);
  }

  addOtherChargeRow(): void {
    const scheduleId = String(this.utilitySchedule?.id ?? '');
    const newCharge: OtherChargeItem = {
      Pending: '',
      ScheduleID: scheduleId,
      ChargeType: '',
      Description: '',
      ChargeUnit: 'per month',
      manually_entered: true,
    };
    this.otherChargesEdit.set([...this.otherChargesEdit(), newCharge]);
  }

  deleteOtherChargeRow(index: number): void {
    const copy = this.otherChargesEdit().slice();
    copy.splice(index, 1);
    this.otherChargesEdit.set(copy);
  }

  updateOtherChargeField(index: number, field: keyof OtherChargeItem, value: string | undefined): void {
    const current = this.otherChargesEdit();
    const copy = current.slice();
    copy[index] = { ...copy[index], [field]: value };
    this.otherChargesEdit.set(copy);
  }

  isOtherChargeDescriptionValid(charge: OtherChargeItem): boolean {
    return ((charge.Description ?? '').trim().length > 0);
  }

  isOtherChargeTypeValid(charge: OtherChargeItem): boolean {
    return ((charge.ChargeType ?? '').trim().length > 0);
  }

  isOtherChargesEditValid(): boolean {
    const list = this.otherChargesEdit();
    if (list.length === 0) return false;
    return list.every((c) => this.isOtherChargeDescriptionValid(c) && this.isOtherChargeTypeValid(c));
  }

  enterNotesEdit(): void {
    this.notesEdit.set(this.notes.map((n) => ({ ...n })));
    this.editingNotes.set(true);
  }

  cancelNotesEdit(): void {
    this.editingNotes.set(false);
    this.notesEdit.set([]);
  }

  private noteUnchanged(orig: NoteItem, edited: NoteItem): boolean {
    const d = (a: string | undefined, b: string | undefined) => (a ?? '').trim() === (b ?? '').trim();
    return d(orig.Description, edited.Description) && d(orig.Type, edited.Type);
  }

  saveNotesEdit(): void {
    const edited = this.notesEdit();
    const original = this.notes;
    const scheduleId = String(this.utilitySchedule?.id ?? '');
    const notes: NoteItem[] = edited.map((n, i) => {
      const isNewOrModified = i >= original.length || !this.noteUnchanged(original[i], n);
      return {
        Pending: n.Pending ?? '',
        ScheduleID: n.ScheduleID || scheduleId,
        Description: (n.Description ?? '').trim(),
        Type: (n.Type ?? '').trim() || undefined,
        manually_entered: isNewOrModified ? true : (original[i].manually_entered ?? false),
      };
    });
    const meta = this.scheduleMetadataValue;
    const updated: ScheduleMetadata = {
      service_charges: meta?.service_charges ?? [],
      other_charges: meta?.other_charges ?? [],
      notes,
      reactive_demand: meta?.reactive_demand ?? [],
      modifies_schedule: meta?.modifies_schedule ?? [],
      tax_info: meta?.tax_info ?? [],
      percentages: meta?.percentages ?? [],
    };
    this.saveNotes.emit(updated);
    this.editingNotes.set(false);
    this.notesEdit.set([]);
  }

  addNoteRow(): void {
    const scheduleId = String(this.utilitySchedule?.id ?? '');
    const newNote: NoteItem = {
      Pending: '',
      ScheduleID: scheduleId,
      Description: '',
      Type: '',
      manually_entered: true,
    };
    this.notesEdit.set([...this.notesEdit(), newNote]);
  }

  deleteNoteRow(index: number): void {
    const copy = this.notesEdit().slice();
    copy.splice(index, 1);
    this.notesEdit.set(copy);
  }

  updateNoteField(index: number, field: keyof NoteItem, value: string | undefined): void {
    const current = this.notesEdit();
    const copy = current.slice();
    copy[index] = { ...copy[index], [field]: value };
    this.notesEdit.set(copy);
  }

  isNoteDescriptionValid(note: NoteItem): boolean {
    return ((note.Description ?? '').trim().length > 0);
  }

  isNotesEditValid(): boolean {
    const list = this.notesEdit();
    if (list.length === 0) return false;
    return list.every((n) => this.isNoteDescriptionValid(n));
  }

  updateDemandRateField(index: number, field: keyof DemandChargeRate, value: string | number | undefined): void {
    const numericFields: (keyof DemandChargeRate)[] = [
      'id', 'utility_schedule_id', 'demand_charge_rate_kw', 'min_kv', 'max_kv',
      'start_month', 'start_day', 'end_month', 'end_day', 'start_kw', 'end_kw'
    ];
    let parsed: string | number | undefined = value;
    if (typeof value === 'string' && numericFields.includes(field)) {
      const num = value === '' ? undefined : Number(value);
      parsed = Number.isNaN(num) ? undefined : num;
    }
    const current = this.demandRatesEdit();
    const copy = current.slice();
    copy[index] = { ...copy[index], [field]: parsed };
    this.demandRatesEdit.set(copy);
    // Keep season range in sync when start/end month/day are updated elsewhere
    if (['start_month', 'start_day', 'end_month', 'end_day'].includes(field)) {
      const ranges = this.demandRatesSeasonRanges().slice();
      ranges[index] = this.rateToSeasonRangeRate(copy[index]);
      this.demandRatesSeasonRanges.set(ranges);
    }
  }

  updateEnergyRateField(index: number, field: keyof EnergyRate, value: string | number | undefined): void {
    const numericFields: (keyof EnergyRate)[] = [
      'id', 'utility_schedule_id', 'energy_rate_kwh', 'min_kv', 'max_kv',
      'start_month', 'start_day', 'end_month', 'end_day', 'start_kwh', 'end_kwh'
    ];
    let parsed: string | number | undefined = value;
    if (typeof value === 'string' && numericFields.includes(field)) {
      const num = value === '' ? undefined : Number(value);
      parsed = Number.isNaN(num) ? undefined : num;
    }
    const current = this.energyRatesEdit();
    const copy = current.slice();
    copy[index] = { ...copy[index], [field]: parsed };
    this.energyRatesEdit.set(copy);
    if (['start_month', 'start_day', 'end_month', 'end_day'].includes(field)) {
      const ranges = this.energyRatesSeasonRanges().slice();
      ranges[index] = this.rateToSeasonRangeRate(copy[index]);
      this.energyRatesSeasonRanges.set(ranges);
    }
  }

  /** Whether the rate represents "All year" (no date range). Works for demand or energy rate. */
  isAllYearSeasonRate(rate: DemandChargeRate | EnergyRate): boolean {
    const desc = (rate.season_description ?? '').trim();
    if (desc.toLowerCase() !== 'all year') return false;
    return (
      Number(rate.start_month) === 1 &&
      Number(rate.start_day) === 1 &&
      Number(rate.end_month) === 12 &&
      Number(rate.end_day) === 31
    );
  }

  /** Normalized value for Applies select (trimmed) so API values match option values. */
  getAppliesValue(daysOfWeekHolidays?: string | null): string {
    return (daysOfWeekHolidays ?? '').trim();
  }

  /** Season type for a rate in edit list. Works for demand or energy rate. */
  getSeasonType(rate: DemandChargeRate | EnergyRate): 'all_year' | 'date_range' {
    return this.isAllYearSeasonRate(rate) ? 'all_year' : 'date_range';
  }

  /** Set season type for a demand rate row; updates rate and season ranges. */
  setSeasonType(index: number, type: 'all_year' | 'date_range'): void {
    const current = this.demandRatesEdit();
    const copy = current.slice();
    const ranges = this.demandRatesSeasonRanges().slice();
    if (type === 'all_year') {
      copy[index] = {
        ...copy[index],
        season_description: 'All year',
        start_month: 1,
        start_day: 1,
        end_month: 12,
        end_day: 31,
      };
      ranges[index] = null;
    } else {
      copy[index] = {
        ...copy[index],
        season_description: copy[index].season_description === 'All year' ? '' : copy[index].season_description,
        start_month: undefined,
        start_day: undefined,
        end_month: undefined,
        end_day: undefined,
      };
      ranges[index] = null;
    }
    this.demandRatesEdit.set(copy);
    this.demandRatesSeasonRanges.set(ranges);
  }

  /** Set season type for an energy rate row; updates rate and season ranges. */
  setEnergySeasonType(index: number, type: 'all_year' | 'date_range'): void {
    const current = this.energyRatesEdit();
    const copy = current.slice();
    const ranges = this.energyRatesSeasonRanges().slice();
    if (type === 'all_year') {
      copy[index] = {
        ...copy[index],
        season_description: 'All year',
        start_month: 1,
        start_day: 1,
        end_month: 12,
        end_day: 31,
      };
      ranges[index] = null;
    } else {
      copy[index] = {
        ...copy[index],
        season_description: copy[index].season_description === 'All year' ? '' : copy[index].season_description,
        start_month: undefined,
        start_day: undefined,
        end_month: undefined,
        end_day: undefined,
      };
      ranges[index] = null;
    }
    this.energyRatesEdit.set(copy);
    this.energyRatesSeasonRanges.set(ranges);
  }

  /** Convert rate start/end month-day to date range for datepicker. Uses next year for end when season wraps (e.g. Nov–Mar). Works for demand or energy rate. */
  private rateToSeasonRangeRate(rate: DemandChargeRate | EnergyRate): { fromDate: string | null; toDate: string | null } | null {
    const { start_month, start_day, end_month, end_day } = rate;
    if (
      start_month == null || start_day == null ||
      end_month == null || end_day == null
    ) {
      return null;
    }
    const from = `${NOMINAL_YEAR}-${String(start_month).padStart(2, '0')}-${String(start_day).padStart(2, '0')}`;
    const isWrap = start_month > end_month || (start_month === end_month && (start_day ?? 0) > (end_day ?? 0));
    const endYear = isWrap ? NOMINAL_YEAR + 1 : NOMINAL_YEAR;
    const to = `${endYear}-${String(end_month).padStart(2, '0')}-${String(end_day).padStart(2, '0')}`;
    return { fromDate: from, toDate: to };
  }

  /** Value for datepicker binding (used with ngModel). */
  getSeasonRange(index: number): { fromDate: string | null; toDate: string | null } | null {
    return this.demandRatesSeasonRanges()[index] ?? null;
  }

  /** Called when demand datepicker range changes; updates rate and ranges array. Auto-collapses when both dates are set. */
  onSeasonRangeChange(index: number, value: { fromDate: string | null; toDate: string | null } | null): void {
    const ranges = this.demandRatesSeasonRanges().slice();
    ranges[index] = value;
    this.demandRatesSeasonRanges.set(ranges);
    if (!value?.fromDate || !value?.toDate) return;
    const parsed = this.parseSeasonRangeValue({ fromDate: value.fromDate, toDate: value.toDate });
    if (!parsed) return;
    const current = this.demandRatesEdit();
    const copy = current.slice();
    copy[index] = { ...copy[index], ...parsed };
    this.demandRatesEdit.set(copy);
    this.expandedSeasonRowIndex.set(null);
  }

  /** Called when energy datepicker range changes; updates rate and ranges array. Auto-collapses when both dates are set. */
  onEnergySeasonRangeChange(index: number, value: { fromDate: string | null; toDate: string | null } | null): void {
    const ranges = this.energyRatesSeasonRanges().slice();
    ranges[index] = value;
    this.energyRatesSeasonRanges.set(ranges);
    if (!value?.fromDate || !value?.toDate) return;
    const parsed = this.parseSeasonRangeValue({ fromDate: value.fromDate, toDate: value.toDate });
    if (!parsed) return;
    const current = this.energyRatesEdit();
    const copy = current.slice();
    copy[index] = { ...copy[index], ...parsed };
    this.energyRatesEdit.set(copy);
    this.expandedEnergySeasonRowIndex.set(null);
  }

  private parseSeasonRangeValue(value: { fromDate: string; toDate: string }): { start_month: number; start_day: number; end_month: number; end_day: number } | null {
    const fromParts = value.fromDate.split('-');
    const toParts = value.toDate.split('-');
    if (fromParts.length !== 3 || toParts.length !== 3) return null;
    const start_month = parseInt(fromParts[1], 10);
    const start_day = parseInt(fromParts[2], 10);
    const end_month = parseInt(toParts[1], 10);
    const end_day = parseInt(toParts[2], 10);
    if (Number.isNaN(start_month) || Number.isNaN(start_day) || Number.isNaN(end_month) || Number.isNaN(end_day)) return null;
    return { start_month, start_day, end_month, end_day };
  }

  toggleSeasonDatepickerExpanded(index: number): void {
    this.expandedSeasonRowIndex.set(this.expandedSeasonRowIndex() === index ? null : index);
  }

  toggleEnergySeasonDatepickerExpanded(index: number): void {
    this.expandedEnergySeasonRowIndex.set(this.expandedEnergySeasonRowIndex() === index ? null : index);
  }

  /** Value for energy datepicker binding. */
  getEnergySeasonRange(index: number): { fromDate: string | null; toDate: string | null } | null {
    return this.energyRatesSeasonRanges()[index] ?? null;
  }

  /** Format season date range for display when datepicker is collapsed (e.g. "Jan 1 - Dec 31", "Nov 1 - Mar 31"). Works for demand or energy rate. */
  formatSeasonRangeDisplay(rate: DemandChargeRate | EnergyRate): string {
    const { start_month, start_day, end_month, end_day } = rate;
    if (
      start_month == null || start_day == null ||
      end_month == null || end_day == null
    ) {
      return 'Select dates';
    }
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const start = `${months[start_month - 1]} ${start_day}`;
    const end = `${months[end_month - 1]} ${end_day}`;
    return `${start} – ${end}`;
  }

  getSeasonTooltipText(rate: DemandChargeRate | EnergyRate): string | null {
    const { start_month, start_day, end_month, end_day } = rate;
    if (
      start_month == null || start_day == null ||
      end_month == null || end_day == null
    ) {
      return null;
    }

    const season = (rate.season_description ?? '').trim().toLowerCase();
    if (!season || season === 'all year') {
      return null;
    }

    if (
      start_month < 1 || start_month > 12 ||
      end_month < 1 || end_month > 12
    ) {
      return null;
    }

    return `${this.monthNames[start_month - 1]} ${start_day} - ${this.monthNames[end_month - 1]} ${end_day}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If metadata is provided as input, use it
    if (changes['scheduleMetadata']) {
      this._scheduleMetadata.set(this.scheduleMetadata);
    }

    // Handle schedule changes
    if (changes['utilitySchedule']) {
      if (this.utilitySchedule) {
        // Schedule is available
        // If metadata is provided as input, it's already set above
        // If metadata is not provided as input, load it
        if (this.autoLoadScheduleMetadata && !this.scheduleMetadata) {
          this.loadScheduleMetadata();
        }
      } else {
        // Schedule is cleared, clear metadata
        this._scheduleMetadata.set(null);
      }
    }
  }

  private loadScheduleMetadata(): void {
    if (!this.utilitySchedule) {
      return;
    }

    // Don't load if metadata is already provided as input
    if (this.scheduleMetadata) {
      return;
    }

    const scheduleId = this.utilitySchedule.id;
    if (scheduleId == null) {
      return;
    }

    this.isLoadingMetadata.set(true);
    this.utilityService
      .getUtilityScheduleDetail(this.utilitySchedule.utility_id, scheduleId)
      .subscribe({
        next: (detail) => {
          this._scheduleMetadata.set(detail.schedule_metadata);
          this.isLoadingMetadata.set(false);
        },
        error: (error) => {
          console.error('Error loading schedule metadata:', error);
          this._scheduleMetadata.set(null);
          this.isLoadingMetadata.set(false);
        },
      });
  }

  /** Empty metadata used when schedule is selected but API returned null (no metadata yet). */
  private static readonly EMPTY_METADATA: ScheduleMetadata = {
    service_charges: [],
    other_charges: [],
    notes: [],
    reactive_demand: [],
    modifies_schedule: [],
    tax_info: [],
    percentages: [],
  };

  // Getter for schedule metadata - uses input if provided, otherwise uses loaded metadata. When we have a schedule but no metadata, use empty so Service Charges section can be shown.
  get scheduleMetadataValue(): ScheduleMetadata | null {
    const raw = this.scheduleMetadata || this._scheduleMetadata();
    if (raw) return raw;
    return this.utilitySchedule ? UtilityRatesDisplayComponent.EMPTY_METADATA : null;
  }

  get serviceCharges(): ServiceChargeItem[] {
    return this.scheduleMetadataValue?.service_charges ?? [];
  }

  get otherCharges(): OtherChargeItem[] {
    return this.scheduleMetadataValue?.other_charges ?? [];
  }

  get notes(): NoteItem[] {
    return this.scheduleMetadataValue?.notes ?? [];
  }

  // Group rates by season
  get demandRatesBySeason(): Array<{ season: string; rates: DemandChargeRate[] }> {
    const grouped = new Map<string, DemandChargeRate[]>();
    for (const rate of this.demandRates) {
      const season = rate.season_description || 'No Season';
      if (!grouped.has(season)) {
        grouped.set(season, []);
      }
      grouped.get(season)!.push(rate);
    }
    return Array.from(grouped.entries()).map(([season, rates]) => ({ season, rates }));
  }

  get energyRatesBySeason(): Array<{ season: string; rates: EnergyRate[] }> {
    const grouped = new Map<string, EnergyRate[]>();
    for (const rate of this.energyRates) {
      const season = rate.season_description || 'No Season';
      if (!grouped.has(season)) {
        grouped.set(season, []);
      }
      grouped.get(season)!.push(rate);
    }
    return Array.from(grouped.entries()).map(([season, rates]) => ({ season, rates }));
  }

  // Format days of week and holidays display
  getAppliesText(daysOfWeekHolidays?: string): string {
    if (!daysOfWeekHolidays) {
      return '-';
    }

    // Special cases
    if (daysOfWeekHolidays === 'YYYYYYYY') {
      return 'Every day';
    }
    if (daysOfWeekHolidays === 'YNNNNNYY') {
      return 'Weekends / Holidays';
    }
    if (daysOfWeekHolidays === 'NYYYYYNN') {
      return 'Weekdays';
    }

    // Parse the string: first 7 chars are days of week (Sun-Sat), last char is holidays
    const days = daysOfWeekHolidays.slice(0, 7);
    const holidays = daysOfWeekHolidays.length > 7 ? daysOfWeekHolidays[7] : 'N';

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const activeDays: string[] = [];

    for (let i = 0; i < days.length; i++) {
      if (days[i] === 'Y') {
        activeDays.push(dayNames[i]);
      }
    }

    const parts: string[] = [];
    if (activeDays.length > 0) {
      parts.push(activeDays.join(', '));
    }
    if (holidays === 'Y') {
      parts.push('Holidays');
    }

    return parts.length > 0 ? parts.join(' / ') : '-';
  }

  getDemandRangeText(rate: DemandChargeRate): string {
    const start = rate.start_kw;
    const end = rate.end_kw;
    if (start == null && end == null) {
      return '-';
    }
    if (start != null && end == null) {
      return `${start}+`;
    }
    if (start == null && end != null) {
      return `0 - ${end}`;
    }
    return `${start} - ${end}`;
  }

  getEnergyRangeText(rate: EnergyRate): string {
    const start = rate.start_kwh;
    const end = rate.end_kwh;
    if (start == null && end == null) {
      return '-';
    }
    if (start != null && end == null) {
      return `${start}+`;
    }
    if (start == null && end != null) {
      return `0 - ${end}`;
    }
    return `${start} - ${end}`;
  }
}

